import { injectable } from 'inversify';
import { Arr } from '../../../CodesForFoundation';
import { debounce } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, Field, ObjectManager } from '../../../concepts/objects';
import { ActiveObject, resolve } from '../../Container';
import { AdapterInfo } from '../AdapterInfo';
import { EditorSession } from '../EditorSession';
import { Selection, SelectionWatch } from '../Selection';
import { TextField } from '../fields/TextField';
import { ToolInfo } from '../tooling/ToolInfo';
import { ToolManager } from '../tooling/ToolManager';
import { WidgetManager } from '../workspace/widgets/WidgetManager';
import { PropertyFacade } from './PropertyFacade';
import { PropertyHistory } from './PropertyHistory';
import { PropertyStripInfo } from './PropertyStripInfo';

const isToolStateBanned = (state: string) =>
    state !== 'idle' &&
    state !== 'panning' &&
    state !== 'selecting' &&
    state !== 'mutating' &&
    !state.endsWith('Primed');

const getNamedOrDefaultTextField = (c: Component, fieldName?: string) => {
    if (fieldName) {
        const field = c.field(fieldName);
        return (field.type === 'text') ? field : getNamedOrDefaultTextField(c);
    }
    else {
        const all = c.fields.filter(x => x instanceof TextField) as TextField[];
        return Arr.first(Arr.sortBy(all, 'default', 'desc'));
    }
};

@injectable()
export class PropertyStripMechanics extends ActiveObject {

    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(PropertyHistory) private propHistory!: PropertyHistory;
    @resolve(ToolManager) private tools!: ToolManager;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    private state = ['none'] as [string, string?];
    private watch = null as SelectionWatch;

    public start(): void {
        this.burden(this.session.observe(AdapterInfo, this.sync));
        this.burden(this.session.observe(PropertyStripInfo, this.sync));
        this.burden(this.session.observe(Selection, this.sync));
        this.burden(this.session.observe(ToolInfo, this.sync));
        this.burden(this.watch = new SelectionWatch(this.session, this.objects, debounce(this.sync)));
        this.burden(this.session.observe(Selection, this.resetModeOnEmptySelection));
    }

    private sync = () => {
        const next = this.getNextStripState();
        if (Arr.equals(next, this.state)) return;
        const [type] = this.state = next;
        if (type === 'none') {
            this.unrealizeStrip();
            this.watch.disable();
        }
        else if (type === 'standard') {
            this.realizeStandardSelectionStrip();
            this.watch.enable();
        }
        else if (type === 'adapter') {
            this.realizeAdapterStrip();
            this.watch.enable();
        }
    };

    private getNextStripState(): [string, string?] {
        const adapter = this.session.get(AdapterInfo);
        const { mode } = this.session.get(PropertyStripInfo);
        const selection = this.session.get(Selection);
        const toolInfo = this.session.get(ToolInfo);
        const tool = this.tools.get(toolInfo.id);
        const key = `${mode}:${selection.id}:${selection.locked()}`;

        if (tool.special) return ['none'];
        if (isToolStateBanned(toolInfo.state)) return ['none'];
        if (selection.size === 0) return ['none'];
        if (mode === 'disabled') return ['none'];
        if (mode === 'field') return ['adapter', key];
        if (adapter.target !== 'none') {
            return adapter.type === 'field' && !adapter.opts.hideProps
                ? ['adapter', `${key}:${adapter.name}`] : ['none'];
        }
        if (selection.uniform()) return ['standard', key];
        return ['none'];
    }

    private unrealizeStrip() {
        this.widgets.delete('propertyStrip');
    }

    private realizeStandardSelectionStrip() {
        const { dispatch, objects, session, widgets } = this;

        this.unrealizeStrip();

        const targets = Array.from(session.get(Selection)).filter(x => x.alive);
        const first = targets[0];
        if (!first) return;

        const { propertyLayout } = Component.describe(first);
        if (!propertyLayout) return;

        const container = widgets.createContainer();
        container.scale = 1.5;
        container.name = 'propertyStrip';
        container.position = ['attached', { to: targets.map(x => x.id), placement: 'top', zOrder: 100, offset: [2.5, 0] }];
        container.direction = 'row';

        const layoutItems = propertyLayout(dispatch, session, objects, widgets, new PropertyFacade(this.propHistory, targets));
        container.addAll(layoutItems);

        widgets.add(container);
    }

    private realizeAdapterStrip() {
        const { dispatch, objects, session, widgets } = this;

        this.unrealizeStrip();

        const targets = Array.from(session.get(Selection)).filter(x => x.alive);
        if (!targets.length) return;
        if (!targets[0].uiFields[0]) return;

        if (targets[0].uiFields[0] instanceof Field) {
            this.realizeAdapterStripForLegacyFields();
        }
        else {
            this.realizeAdapterStripForUIFields();
        }
    }

    private realizeAdapterStripForLegacyFields() {
        const { dispatch, objects, session, widgets } = this;

        this.unrealizeStrip();

        const adapter = session.get(AdapterInfo);
        const named = adapter.target !== 'none' && !!adapter.name ? adapter.name : null;

        const targets = Array.from(session.get(Selection)).filter(x => x.alive);
        const fields = targets.map(x => getNamedOrDefaultTextField(x, named)).filter(x => !!x);
        if (!fields.length) return;

        const desc = Field.describe(fields[0]);
        const layout = desc.propertyLayout;
        if (!layout) return;

        const container = widgets.createContainer();
        container.scale = 1.5;
        container.tag = 'ignore-adapter';
        container.name = 'propertyStrip';
        container.position = ['attached', { to: targets.map(x => x.id), placement: 'top', zOrder: 100, offset: [2.5, 0] }];
        container.direction = 'row';

        const layoutItems = layout(dispatch, session, objects, widgets, new PropertyFacade(this.propHistory, fields));
        container.addAll(layoutItems);

        widgets.add(container);
    }

    private realizeAdapterStripForUIFields() {
        const { dispatch, objects, session, widgets } = this;

        this.unrealizeStrip();

        const adapter = session.get(AdapterInfo);
        const named = adapter.target !== 'none' && !!adapter.name ? adapter.name : null;

        const targets = Array.from(session.get(Selection)).filter(x => x.alive);
        const fields = targets.map(x => x.uiFields.find(f => f.name === named) ?? x.uiFields[0]).filter(x => !!x);
        if (!fields.length) return;

        const layout = fields[0].propertyLayout;
        if (!layout) return;

        const container = widgets.createContainer();
        container.scale = 1.5;
        container.tag = 'ignore-adapter';
        container.name = 'propertyStrip';
        container.position = ['attached', { to: targets.map(x => x.id), placement: 'top', zOrder: 100, offset: [2.5, 0] }];
        container.direction = 'row';

        const layoutItems = layout(dispatch, session, objects, widgets, new PropertyFacade(this.propHistory, targets));
        container.addAll(layoutItems);

        widgets.add(container);
    }

    private resetModeOnEmptySelection = () => {
        const { mode } = this.session.get(PropertyStripInfo);
        const selection = this.session.get(Selection);
        if (mode === 'field' && selection.size === 0) {
            this.session.patch(PropertyStripInfo, { mode: 'auto' });
        }
    };
}
